import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import ChatHelpers from "../lib/chatHelpers"
import MarkerHelpers from "../lib/markerHelpers"
import MessageHelpers from "../lib/messageHelpers"
import { jid } from "@xmpp/client"
import groupBy from "../lib/groupBy"
import { PURGE } from "redux-persist"
import {
  refreshChats,
  RefreshChatsAction,
  upsertChat,
  UpsertChatAction,
} from "./chatsSlice"
import { areMessageFromDifferentDay } from "@/lib/messageDate"
import { RouteStop, Toll } from "@/reducers/routes2gSlice"
import { Location } from "@/reducers/locationsSlice"

export type MarkerName = "received" | "displayed"
export type MessageType = "chat" | "groupchat"
export type SendStatus = "sending" | "sent" | "error"

export type MessageRetraction = {
  id: string
  stanzaId?: string
  from: string
  to: string
  messageId: string
  timestamp: string
  type: string
  displayType: MessageDisplayType.Retraction
}

export type Marker = {
  id: string
  to: string
  from: string
  name: MarkerName
  markedMessageId: string
  stanzaId?: string
  timestamp: string
  type: MessageType
  sendStatus?: SendStatus
  displayType: MessageDisplayType.Marker
}

export type Reference = {
  type: "address"
  begin: number
  end: number
}

export type Reaction = {
  id: string
  to: string
  from: string
  senderJid?: string
  localEcho?: boolean
  author?: string
  messageId: string
  emojis: string[]
  type: MessageType
  timestamp: string
  displayType: MessageDisplayType.Reaction
}

export const reactionSenderKey = (reaction: Reaction): string =>
  MessageHelpers.senderKey(reaction)

const sameReactionSender = (
  first: Reaction,
  second: Reaction,
  myJid: string,
): boolean => {
  if (reactionSenderKey(first) === reactionSenderKey(second)) return true
  if (first.from !== second.from) return false
  const myBareJid = jid.parse(myJid).bare().toString()
  const local = first.localEcho ? first : second.localEcho ? second : undefined
  const remote = local === first ? second : first
  return (
    !!local &&
    (!local.senderJid || local.senderJid === myBareJid) &&
    (!remote.senderJid || remote.senderJid === myBareJid)
  )
}

const recordReaction = (
  reactions: Record<string, Reaction>,
  reaction: Reaction,
  myJid: string,
) => {
  for (const [key, previous] of Object.entries(reactions)) {
    if (sameReactionSender(previous, reaction, myJid)) delete reactions[key]
  }
  reactions[reactionSenderKey(reaction)] = reaction
}

export type MessageFileShared = {
  id: string
  to: string
  from: string
  messageId: string
  fileUrl: string
  userJid: string
  type: MessageType
  timestamp: string
  displayType: MessageDisplayType.MessageFileShared
}

export type ExtractedMetadata = {
  id: string
  to: string
  from: string
  messageId: string
  type: MessageType
  timestamp: string
  documentType: string
  data: Record<string, string> | undefined
  blurry?: boolean
  remarks?: string
  displayType: MessageDisplayType.ExtractedMetadata
}

export type DocumentDriverContext = {
  id: string
  to: string
  from: string
  messageId: string
  type: MessageType
  timestamp: string
  plates: string
  location: Location | undefined
  displayType: MessageDisplayType.DocumentDriverContext
}

export type DocumentLink = {
  id: string
  to: string
  from: string
  cmrMessageId: string
  deliveryOrderMessageId: string
  /** GPS DB order ID; GPS links carry it instead of deliveryOrderMessageId. */
  deliveryOrderId?: string
  cmrFileUrl: string
  cmrFileName: string
  /** Extracted document type of a GPS link, e.g. "CMR" or "Lieferschein". */
  cmrDocumentType?: string
  deliveryOrderFileUrl: string
  deliveryOrderFileName: string
  type: MessageType
  timestamp: string
  displayType: MessageDisplayType.DocumentLink
}

export type DeliveryOrder = {
  id: string
  to: string
  from: string
  messageId: string
  fileUrl: string | null
  fileName: string | null
  files: Array<{ fileUrl: string; fileName: string }> | undefined
  metadata: any
  /** GPS DB order ID from <delivery-order-id>, when the sender included it. */
  deliveryOrderId?: string
  type: MessageType
  timestamp: string
  displayType: MessageDisplayType.DeliveryOrder
}

export type StopUpdate = {
  result:
    | "added"
    | "updated"
    | "cannot_geocode"
    | "imprecise_non_pickup_delivery"
  address: string
  is_approximate: boolean
  action: string
}

export type Fasten = {
  id: string
  to: string
  from: string
  messageId: string
  fastensWith: "RouteUpdated" | "RouteUnchanged"
  payload: null | StopUpdate[]
  type: MessageType
  timestamp: string
  displayType: MessageDisplayType.Fasten
}

export enum MessageDisplayType {
  Message,
  MessageGroup,
  Marker,
  Reaction,
  MessageFileShared,
  Retraction,
  Fasten,
  DeliveryOrder,
  ExtractedMetadata,
  DocumentDriverContext,
  DocumentLink,
  SlickAIGroup,
}

export type Thumbnail = {
  uri: string
  width: number
  height: number
}

export type ReferenceRouteAlert = {
  isOffRoute: boolean
  previousIsOffRoute: boolean
  deltaMeters: number
  updatedAt: string
  isRouteDeltaAlert: boolean
  alertId: string
}

export type StopLateAlert = {
  alertId: string
  stop?: RouteStop
}

export type FinishedRouteAlert = {
  alertId: string
  expectedDistance: number
  expectedDuration: number
  expectedTolls?: Toll[]
  actualTolls?: Toll[]
  actualApproachTolls?: Toll[]
  actualDistance?: number
  actualDuration: number
  actualApproachDistance?: number
  actualApproachDuration?: number
  routeTimestamp: string
  startTimestamp: string
  endTimestamp: string
  firstStopId: string
  lastStopId: string
  expectedRouteDriverJid?: string
  expectedRouteTimestamp?: string
  expectedRouteFirstStopId?: string
  expectedRouteLastStopId?: string
  fileUrl?: string
  fileName?: string
  /** The customer's own order number, when the order came from an integration. */
  transportNumber?: string
}

export type CarReportKind = "fault" | "collision"

export type Message = {
  from: string
  to: string
  type: MessageType
  id: string
  originId?: string
  senderJid?: string
  author?: string | undefined
  body: string
  translation?: string
  /**
   * What the dispatcher actually typed, when `body` is a machine translation
   * of it. Only outgoing messages carry this; the incoming driver flow puts
   * its translation in `translation` instead.
   */
  originalBody?: string
  /** ISO 639-1 code of `originalBody`. */
  originalBodyLang?: string
  thumbnail?: Thumbnail
  markers: Marker[]
  reactions: Reaction[]
  timestamp: string
  forwarded?: Message
  file?: boolean
  url?: string
  fileName?: string
  references?: Reference[]
  replaceId?: string
  missedCallNumber?: string
  replyMessage?: MessageFallback
  displayType: MessageDisplayType.Message
  retracted?: boolean
  active?: boolean
  composing?: boolean
  paused?: boolean
  activity?: string
  internal?: boolean
  thread?: string
  /** Part of the dispatcher↔Slick AI conversation — see `lib/agentThread`. */
  agentChat?: boolean
  referenceRouteAlert?: ReferenceRouteAlert
  stopLateAlert?: StopLateAlert
  finishedRouteAlert?: FinishedRouteAlert
  aiWidgetMap?: import("@/lib/aiWidgetMapParser").MapViewData
  aiWidgetTacho?: import("@/lib/aiWidgetTachoParser").TachoCard
  aiWidgetOrder?: import("@/lib/aiWidgetOrderParser").OrderCard
  cmrLinkRequest?: { fileUrl: string; fileName?: string }
  fuelAlert?: boolean
  /** A driver's fault or collision report forwarded to the Car Issues room. */
  carReport?: CarReportKind
}

export type MessageGroup = {
  messages: Message[]
  displayType: MessageDisplayType.MessageGroup
  groupId: string
}

/**
 * A run of adjacent SlickAI messages, folded into a single chat row.
 *
 * Unlike `MessageGroup`, expanding does NOT emit the messages as separate rows:
 * the group stays one row and renders the bubbles inside itself. Growing
 * `rows.length` on a click makes the virtualised list recompute against stale
 * offsets, which leaves a hole in the chat.
 */
export type SlickAIGroup = {
  messages: Message[]
  displayType: MessageDisplayType.SlickAIGroup
  /** id of the FIRST message — see `collapseSlickAIGroups` */
  groupId: string
  expanded: boolean
}

/** Anything the chat can put on one row, apart from the placeholders. */
export type GroupableRow = Message | MessageGroup | SlickAIGroup

/**
 * Whether a row stands for several messages rather than being one. The single
 * place that knows which display types carry a `messages` array — a third kind
 * of group should only have to be added here.
 */
export const isGroupedRow = (
  row: GroupableRow,
): row is MessageGroup | SlickAIGroup =>
  row.displayType === MessageDisplayType.MessageGroup ||
  row.displayType === MessageDisplayType.SlickAIGroup

export const hasTheId = (m: GroupableRow, id: string): boolean =>
  isGroupedRow(m) ? m.messages.some((msg) => msg.id === id) : m.id === id

export type MessageFallback = {
  id: string
  author: string
  body: string
}

export type GenericMessage =
  | Message
  | Marker
  | Reaction
  | MessageFileShared
  | MessageRetraction
  | DeliveryOrder

export type Participant = {
  name: string
  firstName?: string
  lastName?: string
  jid: string
  phone?: string
  plates?: string
  role: string
  photo?: {
    data?: string
    type?: string
    url?: string
  }
  description?: string
}

export const buildMessages = (
  chatMessages: Partial<ChatMessages>,
): ChatMessages => {
  return {
    messages: [],
    reactions: {},
    messageCorrections: [],
    messageRetractions: {},
    messageIdsWithFileShared: {},
    messageIdsWithDeliveryOrder: {},
    messageIdsWithExtractedMetadata: {},
    messageIdsWithDocumentDriverContext: {},
    fastens: {},
    documentLinksByCmrMessageId: {},
    documentLinksByDeliveryOrderMessageId: {},
    documentLinksByDeliveryOrderId: {},
    ...chatMessages,
  }
}

type MessageId = string
type UserJid = string
type UnassignedReactionsMap = Record<MessageId, Record<UserJid, Reaction>>

export type ChatEvent = Message | Reaction

type MessageFileSharesMap = Record<MessageId, MessageFileShared>
export type MessageRetractionsMap = Record<MessageId, MessageRetraction>
type DocumentDriverContextMap = Record<MessageId, DocumentDriverContext>
type ExtractedMetadataMap = Record<MessageId, ExtractedMetadata>
export type DeliveryOrdersMap = Record<MessageId, DeliveryOrder>
type FastensMap = Record<MessageId, Fasten[]>
type DocumentLinksMap = Record<MessageId, DocumentLink>
type DocumentLinksArrayMap = Record<string, DocumentLink[]>

/** Remove a document link from both maps by cmrMessageId. */
function removeDocumentLink(
  byDeliveryOrder: DocumentLinksArrayMap,
  byCmr: DocumentLinksMap,
  cmrMessageId: string,
) {
  delete byCmr[cmrMessageId]
  for (const key of Object.keys(byDeliveryOrder)) {
    byDeliveryOrder[key] = byDeliveryOrder[key].filter(
      (l) => l.cmrMessageId !== cmrMessageId,
    )
    if (byDeliveryOrder[key].length === 0) {
      delete byDeliveryOrder[key]
    }
  }
}

/** Push a document link into an array map, deduplicating by cmrMessageId (keeping the latest). */
function upsertDocumentLink(
  map: DocumentLinksArrayMap,
  key: string,
  link: DocumentLink,
) {
  if (!map[key]) {
    map[key] = []
  }
  const existing = map[key].findIndex(
    (l) => l.cmrMessageId === link.cmrMessageId,
  )
  if (existing !== -1) {
    if (link.timestamp >= map[key][existing].timestamp) {
      map[key][existing] = link
    }
  } else {
    map[key].push(link)
  }
}

export type ChatMessages = {
  messages: Message[]
  messageCorrections: Message[]
  messageRetractions: MessageRetractionsMap
  reactions: UnassignedReactionsMap
  messageIdsWithFileShared: MessageFileSharesMap
  messageIdsWithDeliveryOrder: DeliveryOrdersMap
  messageIdsWithExtractedMetadata: ExtractedMetadataMap
  messageIdsWithDocumentDriverContext: DocumentDriverContextMap
  fastens: FastensMap
  documentLinksByCmrMessageId: DocumentLinksMap
  documentLinksByDeliveryOrderMessageId: DocumentLinksArrayMap
  // GPS links read from the database, keyed by order DB ID. Optional because
  // persisted chats created before GPS links have no such map.
  documentLinksByDeliveryOrderId?: DocumentLinksArrayMap
  lastNonMessageEvent?: ChatEvent
}

export type MessagesState = Record<string, ChatMessages>

type LimitChatSizeAction = {
  jid: string
  limit: number
}

export type AddMessageAction = {
  message: Message
  myJid: string
  mode?: "append" | "prepend"
}

type AddBatchMessagesToChatAction = {
  messages: Message[]
  myJid: string
  mode: "append" | "prepend"
}

type MarkMessageAction = {
  marker: Marker
  myJid: string
}

type BatchMarkMessageAction = {
  markers: Marker[]
  myJid: string
}

type AddMessageCorrectionAction = Omit<AddMessageAction, "mode">
type BatchAddMessageCorrectionAction = {
  corrections: Message[]
  myJid: string
}

type AddMessageRetractionAction = {
  retraction: MessageRetraction
  myJid: string
}

const initialState: MessagesState = {}

const handleChatEvent = (chatMessages: ChatMessages, event: ChatEvent) => {
  if (
    !chatMessages.lastNonMessageEvent ||
    chatMessages.lastNonMessageEvent.timestamp < event.timestamp
  ) {
    if (
      event.displayType === MessageDisplayType.Reaction &&
      event.emojis.length === 0
    ) {
      if (
        chatMessages.lastNonMessageEvent?.displayType ===
          MessageDisplayType.Reaction &&
        chatMessages.lastNonMessageEvent.messageId === event.messageId
      ) {
        chatMessages.lastNonMessageEvent = undefined
      }

      return
    }

    chatMessages.lastNonMessageEvent = event
  }
}

const isSameMessage = (a: Message, b: Message): boolean => {
  if (a.originId && b.originId && a.originId === b.originId) return true

  return a.id === b.id
}

const updateStateUsingMarker = (
  state: MessagesState,
  marker: Marker,
  myJid: string,
) => {
  const chatJid = ChatHelpers.getChatJidFromMessage(marker, myJid)

  if (!state[chatJid]) {
    console.error("Can not find a chat marker corresponds to: ", marker)
    return
  }

  // There may be a situation when message is duplicated in the state
  // (server sent it twice with the same ID or some internal bug in the client)
  // In such case add ChatMarker to all of the found messages.
  const messageIndexes = state[chatJid].messages.reduce(
    (indexes: number[], message: Message, i: number) => {
      if (message.id === marker.markedMessageId) {
        indexes.push(i)
      }
      return indexes
    },
    [],
  )

  if (messageIndexes.length === 0) {
    return
  }

  messageIndexes.forEach((index: number) => {
    const message = state[chatJid].messages[index]
    const markerIndex = message.markers.findIndex(
      (m: any) =>
        MarkerHelpers.isDuplicate(m, marker) ||
        MarkerHelpers.isEqual(m, marker),
    )

    if (markerIndex !== -1) {
      message.markers[markerIndex] = marker

      if (!MessageHelpers.isIncoming(marker) && !marker.sendStatus) {
        message.markers[markerIndex].sendStatus = "sent"
      }
    } else {
      message.markers.push(marker)
    }
  })

  compactMarkers(state[chatJid], marker.from)
}

const updateStateUsingCorrection = (
  state: MessagesState,
  correction: Message,
  myJid: string,
) => {
  const chatJid = ChatHelpers.getChatJidFromMessage(correction, myJid)

  if (!state[chatJid]) {
    console.error("Can not find a chat message corresponds to: ", correction)
    return
  }

  state[chatJid].messageCorrections.push(correction)
}

const shouldCollapseMessages = (
  message1: Message,
  message2: Message,
): boolean => {
  if (message1.retracted || message2.retracted) return false

  const isFileGroupable = (message1: Message, message2: Message): boolean => {
    const groupableRegex = /\.gif|\.png|\.jpg|\.jpeg|\.webp|\.pdf/i

    return (
      !!message1.url &&
      groupableRegex.test(message1.url) &&
      !!message2.url &&
      groupableRegex.test(message2.url)
    )
  }
  if (message1.replyMessage || message2.replyMessage) {
    return false
  }

  if (
    (message1.forwarded && !message2.forwarded) ||
    (!message1.forwarded && message2.forwarded)
  ) {
    return false
  }

  if (message1.forwarded && message2.forwarded) {
    message1 = message1.forwarded
    message2 = message2.forwarded
  }

  return (
    isFileGroupable(message1, message2) &&
    message1.from === message2.from &&
    (Date.parse(message1.timestamp) - Date.parse(message2.timestamp)) / 1000 <=
      300
  )
}

export const collapseImageGroups = (
  messages: Message[],
  expandedGroups: Set<string>,
): Array<Message | MessageGroup> => {
  const grouped = messages.reduce((acc: Message[][], message: Message) => {
    if (acc.length === 0) {
      acc.push([message])
    } else {
      const lastGroup = acc[acc.length - 1]
      if (shouldCollapseMessages(message, lastGroup[lastGroup.length - 1])) {
        lastGroup.push(message)
      } else {
        acc.push([message])
      }
    }

    return acc
  }, [])

  return grouped.flatMap((group) => {
    if (group.length === 1) {
      return group[0]
    }

    const currentGroupId = group[group.length - 1].id

    if (expandedGroups.has(currentGroupId)) {
      return group
    }

    return {
      messages: group,
      displayType: MessageDisplayType.MessageGroup,
      groupId: currentGroupId,
    } as MessageGroup
  })
}

export const isSlickAIGroup = (
  row: Message | MessageGroup | SlickAIGroup,
): row is SlickAIGroup => row.displayType === MessageDisplayType.SlickAIGroup

/**
 * Folds every run of adjacent SlickAI messages into one row, so the dispatcher
 * sees a single quiet line instead of the automations themselves.
 *
 * Adjacency is the only criterion — no time window. Ordinary messages stand
 * between the automations anyway, and they already break the run.
 *
 * A lone automation is folded too: the loudest case is a single fuel alarm, and
 * leaving that one on top would defeat the point.
 */
export const collapseSlickAIGroups = (
  rows: Array<Message | MessageGroup>,
  expandedGroups: Set<string>,
): Array<Message | MessageGroup | SlickAIGroup> => {
  const output: Array<Message | MessageGroup | SlickAIGroup> = []
  let run: Message[] = []

  const flush = () => {
    if (run.length === 0) return

    // Keyed by the FIRST message, unlike `collapseImageGroups`. Keying by the
    // last one would change the key when a new automation arrives, collapsing
    // an expanded group under the dispatcher's fingers.
    const groupId = run[0].id

    output.push({
      messages: run,
      displayType: MessageDisplayType.SlickAIGroup,
      groupId,
      expanded: expandedGroups.has(groupId),
    })
    run = []
  }

  for (const row of rows) {
    // `collapseImageGroups` has already run, so a run of Slick's own
    // attachments (scan-check) arrives here folded into a `MessageGroup`.
    // Those are automations too — unfolded they would stay fully visible AND
    // split the run around them.
    const candidates = isGroupedRow(row) ? row.messages : [row]
    const slickAIMessages = candidates.every((m) =>
      ChatHelpers.isSilenceableSlickAIMessage(m),
    )
      ? candidates
      : undefined

    if (slickAIMessages) {
      for (const message of slickAIMessages) {
        // The day separator is drawn between rows, so a group spanning midnight
        // would swallow it. Break the run instead.
        const previous = run[run.length - 1]
        if (previous && areMessageFromDifferentDay(previous, message)) flush()

        run.push(message)
      }
    } else {
      flush()
      output.push(row)
    }
  }
  flush()

  return output
}

/**
 * The chat body's row pipeline in one named place: image runs fold first, then
 * Slick's automations — order matters, a run of Slick's own attachments has to
 * reach the automation fold already grouped.
 *
 * Named and tested on purpose. The automation fold used to be wired inline in
 * `ChatHistory`, and a later refactor dropped that wiring without a single test
 * turning red: every fuel alert came back into the driver rooms of accounts
 * that had asked for silence.
 */
export const buildChatRows = (
  messages: Message[],
  options: {
    expandedImageGroups: Set<string>
    foldAutomations: boolean
    expandedSlickAIGroups: Set<string>
  },
): GroupableRow[] => {
  // The copy is the caller's guarantee: callers append placeholders to the
  // result, and `messages` comes straight out of the store.
  const imageGrouped = collapseImageGroups(
    messages.slice(),
    options.expandedImageGroups,
  )

  return options.foldAutomations
    ? collapseSlickAIGroups(imageGrouped, options.expandedSlickAIGroups)
    : imageGrouped
}

const isMarked = (
  markers: Marker[],
  whom: string,
  name: Marker["name"],
): boolean => {
  return markers.some((marker) => marker.from === whom && marker.name === name)
}

const filterMakers = (
  markers: Marker[],
  whom: string,
  name: Set<Marker["name"]>,
): Marker[] => {
  return markers.filter(
    (marker) =>
      marker.from !== whom || (marker.from === whom && !name.has(marker.name)),
  )
}

export const compactMarkers = (chat: ChatMessages, whom: string) => {
  const cleanableTypes = new Set<Marker["name"]>()
  for (let i = chat.messages.length - 1; i >= 0; i--) {
    const message = chat.messages[i]

    message.markers = filterMakers(message.markers, whom, cleanableTypes)

    if (
      !cleanableTypes.has("displayed") &&
      isMarked(message.markers, whom, "displayed")
    ) {
      cleanableTypes.add("received")
      // we can remove all received markers from current message, since we have the displayed
      message.markers = filterMakers(message.markers, whom, cleanableTypes)
      cleanableTypes.add("displayed")
    }
    if (
      !cleanableTypes.has("received") &&
      isMarked(message.markers, whom, "received")
    ) {
      cleanableTypes.add("received")
    }
  }
}

const messagesSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<AddMessageAction>) => {
      const chatJid = ChatHelpers.getChatJidFromMessage(
        action.payload.message,
        action.payload.myJid,
      )

      if (!state[chatJid]) {
        console.error(
          "Can not find a chat message corresponds to: ",
          action.payload.message,
        )
        return
      }

      // There maybe situations when server resend message to the client for ex. due to stream connection resumed
      // in such cases it is client's responsibility to make sure there are no duplicates
      // so we check first if we already have a message with such id
      const messageIndex = state[chatJid].messages.findIndex((message) =>
        isSameMessage(message, action.payload.message),
      )

      if (messageIndex !== -1) {
        // Found message already in store, overwrite with what we are given
        // We overwrite because what we have in store might be a fake message
        // added there on button send click, and we want to store whatever the
        // server send since it is source of truth.
        state[chatJid].messages[messageIndex] = action.payload.message
        return
      }

      if (action.payload.mode === "prepend") {
        state[chatJid].messages.unshift(action.payload.message)
      } else {
        state[chatJid].messages.push(action.payload.message)
      }
    },
    markMessage: (state, action: PayloadAction<MarkMessageAction>) => {
      updateStateUsingMarker(state, action.payload.marker, action.payload.myJid)
    },
    multiMarkMessage: (
      state,
      action: PayloadAction<BatchMarkMessageAction>,
    ) => {
      action.payload.markers.forEach((marker) => {
        updateStateUsingMarker(state, marker, action.payload.myJid)
      })
    },
    addBatchMessageReactions: (
      state,
      action: PayloadAction<{ reactions: Reaction[]; myJid: string }>,
    ) => {
      const chatsWithReactions = Object.entries(
        groupBy(action.payload.reactions, (reaction) => reaction.from),
      ).map(([chatJid, reactions]) => {
        const reactionChatJid = jid.parse(chatJid).bare().toString()
        const chat = state[reactionChatJid]
        if (!chat)
          throw new Error(`Cannot find corresponding chat for jid ${chatJid}`)

        return { chat, reactions }
      })

      for (const { chat, reactions } of chatsWithReactions) {
        for (const reaction of reactions) {
          if (!chat.reactions[reaction.messageId])
            chat.reactions[reaction.messageId] = {}
          recordReaction(
            chat.reactions[reaction.messageId],
            reaction,
            action.payload.myJid,
          )

          handleChatEvent(chat, reaction)
        }

        for (const message of chat.messages) {
          const reactions = chat.reactions[message.id]

          if (reactions) {
            delete chat.reactions[message.id]

            const replacements = Object.values(reactions)
            message.reactions = message.reactions.filter(
              (reaction) =>
                !replacements.some((replacement) =>
                  sameReactionSender(
                    reaction,
                    replacement,
                    action.payload.myJid,
                  ),
                ),
            )
            message.reactions.push(...replacements)
          }
        }
      }
    },
    addBatchMessageFileShares: (
      state,
      action: PayloadAction<{ messageFileShares: MessageFileShared[] }>,
    ) => {
      const chatsWithShares = Object.entries(
        groupBy(action.payload.messageFileShares, (share) => share.from),
      ).map(([chatJid, shares]) => {
        const shareChatJid = jid.parse(chatJid).bare().toString()
        const chat = state[shareChatJid]
        if (!chat)
          throw new Error(`Cannot find corresponding chat for jid ${chatJid}`)

        return { chat, shares }
      })

      for (const { chat, shares } of chatsWithShares) {
        for (const share of shares) {
          if (!chat.messageIdsWithFileShared) {
            chat.messageIdsWithFileShared = {}
          }

          chat.messageIdsWithFileShared[share.messageId] = share
        }
      }
    },
    addBatchMessageRetractions: (
      state,
      action: PayloadAction<{ messageRetractions: MessageRetraction[] }>,
    ) => {
      const chatsWithRetractions = Object.entries(
        groupBy(
          action.payload.messageRetractions,
          (retraction) => retraction.from,
        ),
      ).map(([chatJid, retractions]) => {
        const chat = state[jid.parse(chatJid).bare().toString()]
        if (!chat) {
          throw new Error(`Cannot find corresponding chat for jid ${chatJid}`)
        }

        return { chat, retractions }
      })

      for (const { chat, retractions } of chatsWithRetractions) {
        for (const retraction of retractions) {
          if (!chat.messageRetractions) {
            chat.messageRetractions = {}
          }

          chat.messageRetractions[retraction.messageId] = retraction
        }
      }
    },
    addBatchExtractedMetadata: (
      state,
      action: PayloadAction<{ extractedMetadata: ExtractedMetadata[] }>,
    ) => {
      const chatsWithExtractedMetadata = Object.entries(
        groupBy(action.payload.extractedMetadata, (metadata) => metadata.from),
      ).map(([chatJid, extractedMetadata]) => {
        const extractedMetadataChatJid = jid.parse(chatJid).bare().toString()
        const chat = state[extractedMetadataChatJid]
        if (!chat)
          throw new Error(`Cannot find corresponding chat for jid ${chatJid}`)

        return { chat, extractedMetadata }
      })

      for (const { chat, extractedMetadata } of chatsWithExtractedMetadata) {
        for (const metadata of extractedMetadata) {
          if (!chat.messageIdsWithExtractedMetadata) {
            chat.messageIdsWithExtractedMetadata = {}
          }

          chat.messageIdsWithExtractedMetadata[metadata.messageId] = metadata
        }
      }
    },
    addBatchDocumentDriverContext: (
      state,
      action: PayloadAction<{
        documentDriverContexts: DocumentDriverContext[]
      }>,
    ) => {
      const chatsWithDocumentDriverContext = Object.entries(
        groupBy(
          action.payload.documentDriverContexts,
          (documentDriverContext) => documentDriverContext.from,
        ),
      ).map(([chatJid, documentDriverContexts]) => {
        const documentDriverContextChatJid = jid
          .parse(chatJid)
          .bare()
          .toString()
        const chat = state[documentDriverContextChatJid]
        if (!chat)
          throw new Error(`Cannot find corresponding chat for jid ${chatJid}`)

        return { chat, documentDriverContexts }
      })

      for (const {
        chat,
        documentDriverContexts,
      } of chatsWithDocumentDriverContext) {
        for (const documentDriverContext of documentDriverContexts) {
          if (!chat.messageIdsWithDocumentDriverContext) {
            chat.messageIdsWithDocumentDriverContext = {}
          }

          chat.messageIdsWithDocumentDriverContext[
            documentDriverContext.messageId
          ] = documentDriverContext
        }
      }
    },
    addBatchDeliveryOrders: (
      state,
      action: PayloadAction<{ deliveryOrders: DeliveryOrder[] }>,
    ) => {
      const chatsWithDeliveryOrders = Object.entries(
        groupBy(
          action.payload.deliveryOrders,
          (deliveryOrder) => deliveryOrder.from,
        ),
      ).map(([chatJid, deliveryOrders]) => {
        const deliveryOrderChatJid = jid.parse(chatJid).bare().toString()
        const chat = state[deliveryOrderChatJid]
        if (!chat)
          throw new Error(`Cannot find corresponding chat for jid ${chatJid}`)

        return { chat, deliveryOrders }
      })

      for (const { chat, deliveryOrders } of chatsWithDeliveryOrders) {
        for (const deliveryOrder of deliveryOrders) {
          if (!chat.messageIdsWithDeliveryOrder) {
            chat.messageIdsWithDeliveryOrder = {}
          }

          chat.messageIdsWithDeliveryOrder[deliveryOrder.messageId] =
            deliveryOrder
        }
      }
    },
    addBatchDocumentLinks: (
      state,
      action: PayloadAction<{ documentLinks: DocumentLink[] }>,
    ) => {
      const chatsWithDocumentLinks = Object.entries(
        groupBy(action.payload.documentLinks, (link) => link.from),
      ).map(([chatJid, documentLinks]) => {
        const linkChatJid = jid.parse(chatJid).bare().toString()
        const chat = state[linkChatJid]
        if (!chat)
          throw new Error(`Cannot find corresponding chat for jid ${chatJid}`)

        return { chat, documentLinks }
      })

      for (const { chat, documentLinks } of chatsWithDocumentLinks) {
        for (const link of documentLinks) {
          if (!chat.documentLinksByCmrMessageId) {
            chat.documentLinksByCmrMessageId = {}
          }
          if (!chat.documentLinksByDeliveryOrderMessageId) {
            chat.documentLinksByDeliveryOrderMessageId = {}
          }
          if (!link.deliveryOrderMessageId) {
            removeDocumentLink(
              chat.documentLinksByDeliveryOrderMessageId,
              chat.documentLinksByCmrMessageId,
              link.cmrMessageId,
            )
          } else {
            chat.documentLinksByCmrMessageId[link.cmrMessageId] = link
            upsertDocumentLink(
              chat.documentLinksByDeliveryOrderMessageId,
              link.deliveryOrderMessageId,
              link,
            )
          }
        }
      }
    },
    /** Replace a room's GPS links with a fresh read from the database. */
    setGpsDocumentLinks: (
      state,
      action: PayloadAction<{ chatJid: string; links: DocumentLink[] }>,
    ) => {
      const chat = state[action.payload.chatJid]
      if (!chat) return
      if (!chat.documentLinksByCmrMessageId) {
        chat.documentLinksByCmrMessageId = {}
      }
      if (!chat.documentLinksByDeliveryOrderMessageId) {
        chat.documentLinksByDeliveryOrderMessageId = {}
      }
      for (const [cmrMessageId, link] of Object.entries(
        chat.documentLinksByCmrMessageId,
      )) {
        if (link.deliveryOrderId) {
          delete chat.documentLinksByCmrMessageId[cmrMessageId]
        }
      }
      chat.documentLinksByDeliveryOrderId = {}
      for (const link of action.payload.links) {
        if (!link.deliveryOrderId) continue
        // The database is the current state, also over an older wizard link.
        removeDocumentLink(
          chat.documentLinksByDeliveryOrderMessageId,
          chat.documentLinksByCmrMessageId,
          link.cmrMessageId,
        )
        chat.documentLinksByCmrMessageId[link.cmrMessageId] = link
        upsertDocumentLink(
          chat.documentLinksByDeliveryOrderId,
          link.deliveryOrderId,
          link,
        )
      }
    },
    addBatchFastens: (state, action: PayloadAction<{ fastens: Fasten[] }>) => {
      const chatsWithRouteUpdates = Object.entries(
        groupBy(action.payload.fastens, (routeUpdate) => routeUpdate.from),
      ).map(([chatJid, fastens]) => {
        const routeUpdateChatJid = jid.parse(chatJid).bare().toString()
        const chat = state[routeUpdateChatJid]
        if (!chat)
          throw new Error(`Cannot find corresponding chat for jid ${chatJid}`)

        return { chat, fastens }
      })

      for (const { chat, fastens } of chatsWithRouteUpdates) {
        for (const fasten of fastens) {
          if (!chat.fastens) {
            chat.fastens = {}
          }

          if (!chat.fastens[fasten.messageId]) {
            chat.fastens[fasten.messageId] = []
          }

          const existingFastens = chat.fastens[fasten.messageId].filter((f) => {
            return f.fastensWith !== fasten.fastensWith
          })
          existingFastens.push(fasten)
          chat.fastens[fasten.messageId] = existingFastens
        }
      }
    },
    addMessageRetraction: (
      state,
      action: PayloadAction<AddMessageRetractionAction>,
    ) => {
      const chatJid = ChatHelpers.getChatJidFromMessage(
        action.payload.retraction,
        action.payload.myJid,
      )

      if (!state[chatJid]) {
        console.error(
          "Can not find a chat marker corresponds to: ",
          action.payload.retraction,
        )
        return
      }

      state[chatJid].messageRetractions[action.payload.retraction.messageId] =
        action.payload.retraction
    },
    limitChatSize: (state, action: PayloadAction<LimitChatSizeAction>) => {
      const jid = action.payload.jid

      const chat = state[jid]
      if (!chat) return

      if (action.payload.limit === 0) {
        chat.messages = []
      }

      if (chat.messages.length > action.payload.limit) {
        chat.messages = chat.messages.slice(-action.payload.limit)
      }
    },
    addBatchMessagesToChat: (
      state,
      action: PayloadAction<AddBatchMessagesToChatAction>,
    ) => {
      const groupedByChat = action.payload.messages.reduce(
        (acc, message) => {
          const chatJid = ChatHelpers.getChatJidFromMessage(
            message,
            action.payload.myJid,
          )

          if (acc[chatJid] === undefined) acc[chatJid] = [] as Message[]
          acc[chatJid].push(message)

          return acc
        },
        {} as { [key: string]: Message[] },
      )

      for (const [chatJid, messages] of Object.entries(groupedByChat)) {
        for (const message of messages) {
          const index = state[chatJid].messages.findIndex((m) =>
            isSameMessage(message, m),
          )
          if (index !== -1) {
            state[chatJid].messages[index] = message
          }
        }

        // dedupe messages
        const filteredMessages = messages.filter((message) => {
          return (
            state[chatJid].messages.findIndex((m) => m.id === message.id) === -1
          )
        })

        // Attach reactions to newly encountered messages
        for (const message of filteredMessages) {
          const reactions = state[chatJid].reactions[message.id]

          if (reactions) {
            delete state[chatJid].reactions[message.id]
            message.reactions.push(...Object.values(reactions))
          }
        }

        if (action.payload.mode === "prepend") {
          state[chatJid].messages.unshift(...filteredMessages)
        } else {
          state[chatJid].messages.push(...filteredMessages)
        }

        state[chatJid].messages.sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
        )
      }
    },
    addMessageCorrection: (
      state,
      action: PayloadAction<AddMessageCorrectionAction>,
    ) => {
      updateStateUsingCorrection(
        state,
        action.payload.message,
        action.payload.myJid,
      )
    },
    multiAddMessageCorrection: (
      state,
      action: PayloadAction<BatchAddMessageCorrectionAction>,
    ) => {
      action.payload.corrections.forEach((correction) => {
        updateStateUsingCorrection(state, correction, action.payload.myJid)
      })
    },
    processArchivedBatch: (
      state,
      action: PayloadAction<ArchivedBatchPayload>,
    ) => {
      const {
        messages,
        corrections,
        reactions,
        retractions,
        fileShares,
        extractedMetadata,
        documentDriverContext,
        deliveryOrders,
        documentLinks,
        fastens,
        markers,
        myJid,
        mode,
        anchor,
      } = action.payload

      // An older page is fetched before the oldest message the chat held when
      // scrolling started. A full room sync may have replaced the history
      // while that page was in flight; prepending it to the fresh history
      // would leave a silent gap between the two. Drop it instead — the next
      // scroll fetches the page before the fresh history.
      if (
        mode === "prepend" &&
        anchor &&
        !state[anchor.chatJid]?.messages.some((m) => m.id === anchor.messageId)
      ) {
        return
      }

      // Process retractions first
      if (retractions.length > 0) {
        const chatsWithRetractions = Object.entries(
          groupBy(retractions, (retraction) => retraction.from),
        ).map(([chatJid, retractions]) => {
          const chat = state[jid.parse(chatJid).bare().toString()]
          if (!chat) {
            throw new Error(`Cannot find corresponding chat for jid ${chatJid}`)
          }
          return { chat, retractions }
        })

        for (const { chat, retractions } of chatsWithRetractions) {
          for (const retraction of retractions) {
            if (!chat.messageRetractions) {
              chat.messageRetractions = {}
            }
            chat.messageRetractions[retraction.messageId] = retraction
          }
        }
      }

      // Process messages
      if (messages.length > 0) {
        const groupedByChat = messages.reduce(
          (acc, message) => {
            const chatJid = ChatHelpers.getChatJidFromMessage(message, myJid)
            if (acc[chatJid] === undefined) acc[chatJid] = [] as Message[]
            acc[chatJid].push(message)
            return acc
          },
          {} as { [key: string]: Message[] },
        )

        for (const [chatJid, msgs] of Object.entries(groupedByChat)) {
          for (const message of msgs) {
            const index = state[chatJid].messages.findIndex((m) =>
              isSameMessage(message, m),
            )
            if (index !== -1) {
              state[chatJid].messages[index] = message
            }
          }

          // dedupe messages
          const filteredMessages = msgs.filter((message) => {
            return (
              state[chatJid].messages.findIndex((m) => m.id === message.id) ===
              -1
            )
          })

          // Attach reactions to newly encountered messages
          for (const message of filteredMessages) {
            const messageReactions = state[chatJid].reactions[message.id]
            if (messageReactions) {
              delete state[chatJid].reactions[message.id]
              message.reactions.push(...Object.values(messageReactions))
            }
          }

          if (mode === "prepend") {
            state[chatJid].messages.unshift(...filteredMessages)
          } else {
            state[chatJid].messages.push(...filteredMessages)
          }

          state[chatJid].messages.sort(
            (a, b) =>
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
          )
        }
      }

      // Process corrections
      if (corrections.length > 0) {
        corrections.forEach((correction) => {
          updateStateUsingCorrection(state, correction, myJid)
        })
      }

      // Process reactions
      if (reactions.length > 0) {
        const chatsWithReactions = Object.entries(
          groupBy(reactions, (reaction) => reaction.from),
        ).map(([chatJid, reactions]) => {
          const reactionChatJid = jid.parse(chatJid).bare().toString()
          const chat = state[reactionChatJid]
          if (!chat)
            throw new Error(`Cannot find corresponding chat for jid ${chatJid}`)

          return { chat, reactions }
        })

        for (const { chat, reactions } of chatsWithReactions) {
          for (const reaction of reactions) {
            if (!chat.reactions[reaction.messageId])
              chat.reactions[reaction.messageId] = {}
            recordReaction(
              chat.reactions[reaction.messageId],
              reaction,
              action.payload.myJid,
            )

            handleChatEvent(chat, reaction)
          }

          for (const message of chat.messages) {
            const messageReactions = chat.reactions[message.id]

            if (messageReactions) {
              delete chat.reactions[message.id]

              const replacements = Object.values(messageReactions)
              message.reactions = message.reactions.filter(
                (reaction) =>
                  !replacements.some((replacement) =>
                    sameReactionSender(
                      reaction,
                      replacement,
                      action.payload.myJid,
                    ),
                  ),
              )
              message.reactions.push(...replacements)
            }
          }
        }
      }

      // Process file shares
      if (fileShares.length > 0) {
        const chatsWithShares = Object.entries(
          groupBy(fileShares, (share) => share.from),
        ).map(([chatJid, shares]) => {
          const shareChatJid = jid.parse(chatJid).bare().toString()
          const chat = state[shareChatJid]
          if (!chat)
            throw new Error(`Cannot find corresponding chat for jid ${chatJid}`)

          return { chat, shares }
        })

        for (const { chat, shares } of chatsWithShares) {
          for (const share of shares) {
            if (!chat.messageIdsWithFileShared) {
              chat.messageIdsWithFileShared = {}
            }

            chat.messageIdsWithFileShared[share.messageId] = share
          }
        }
      }

      // Process extracted metadata
      if (extractedMetadata.length > 0) {
        const chatsWithExtractedMetadata = Object.entries(
          groupBy(extractedMetadata, (metadata) => metadata.from),
        ).map(([chatJid, extractedMetadata]) => {
          const extractedMetadataChatJid = jid.parse(chatJid).bare().toString()
          const chat = state[extractedMetadataChatJid]
          if (!chat)
            throw new Error(`Cannot find corresponding chat for jid ${chatJid}`)

          return { chat, extractedMetadata }
        })

        for (const { chat, extractedMetadata } of chatsWithExtractedMetadata) {
          for (const metadata of extractedMetadata) {
            if (!chat.messageIdsWithExtractedMetadata) {
              chat.messageIdsWithExtractedMetadata = {}
            }

            chat.messageIdsWithExtractedMetadata[metadata.messageId] = metadata
          }
        }
      }

      // Process document driver contexts
      if (documentDriverContext.length > 0) {
        const chatsWithDocumentDriverContext = Object.entries(
          groupBy(documentDriverContext, (driverContext) => driverContext.from),
        ).map(([chatJid, documentDriverContext]) => {
          const documentDriverContextChatJid = jid
            .parse(chatJid)
            .bare()
            .toString()
          const chat = state[documentDriverContextChatJid]
          if (!chat)
            throw new Error(`Cannot find corresponding chat for jid ${chatJid}`)

          return { chat, documentDriverContext }
        })

        for (const {
          chat,
          documentDriverContext,
        } of chatsWithDocumentDriverContext) {
          for (const context of documentDriverContext) {
            if (!chat.messageIdsWithDocumentDriverContext) {
              chat.messageIdsWithDocumentDriverContext = {}
            }

            chat.messageIdsWithDocumentDriverContext[context.messageId] =
              context
          }
        }
      }

      // Process delivery orders
      if (deliveryOrders.length > 0) {
        const chatsWithDeliveryOrders = Object.entries(
          groupBy(deliveryOrders, (deliveryOrder) => deliveryOrder.from),
        ).map(([chatJid, deliveryOrders]) => {
          const deliveryOrderChatJid = jid.parse(chatJid).bare().toString()
          const chat = state[deliveryOrderChatJid]
          if (!chat)
            throw new Error(`Cannot find corresponding chat for jid ${chatJid}`)

          return { chat, deliveryOrders }
        })

        for (const { chat, deliveryOrders } of chatsWithDeliveryOrders) {
          for (const deliveryOrder of deliveryOrders) {
            if (!chat.messageIdsWithDeliveryOrder) {
              chat.messageIdsWithDeliveryOrder = {}
            }

            chat.messageIdsWithDeliveryOrder[deliveryOrder.messageId] =
              deliveryOrder
          }
        }
      }

      // Process document links
      if (documentLinks.length > 0) {
        const chatsWithDocumentLinks = Object.entries(
          groupBy(documentLinks, (link) => link.from),
        ).map(([chatJid, documentLinks]) => {
          const linkChatJid = jid.parse(chatJid).bare().toString()
          const chat = state[linkChatJid]
          if (!chat)
            throw new Error(`Cannot find corresponding chat for jid ${chatJid}`)

          return { chat, documentLinks }
        })

        for (const { chat, documentLinks } of chatsWithDocumentLinks) {
          for (const link of documentLinks) {
            if (!chat.documentLinksByCmrMessageId) {
              chat.documentLinksByCmrMessageId = {}
            }
            if (!chat.documentLinksByDeliveryOrderMessageId) {
              chat.documentLinksByDeliveryOrderMessageId = {}
            }
            if (!link.deliveryOrderMessageId) {
              removeDocumentLink(
                chat.documentLinksByDeliveryOrderMessageId,
                chat.documentLinksByCmrMessageId,
                link.cmrMessageId,
              )
            } else {
              chat.documentLinksByCmrMessageId[link.cmrMessageId] = link
              upsertDocumentLink(
                chat.documentLinksByDeliveryOrderMessageId,
                link.deliveryOrderMessageId,
                link,
              )
            }
          }
        }
      }

      // Process fastens
      if (fastens.length > 0) {
        const chatsWithRouteUpdates = Object.entries(
          groupBy(fastens, (routeUpdate) => routeUpdate.from),
        ).map(([chatJid, fastens]) => {
          const routeUpdateChatJid = jid.parse(chatJid).bare().toString()
          const chat = state[routeUpdateChatJid]
          if (!chat)
            throw new Error(`Cannot find corresponding chat for jid ${chatJid}`)

          return { chat, fastens }
        })

        for (const { chat, fastens } of chatsWithRouteUpdates) {
          for (const fasten of fastens) {
            if (!chat.fastens) {
              chat.fastens = {}
            }

            if (!chat.fastens[fasten.messageId]) {
              chat.fastens[fasten.messageId] = []
            }

            const existingFastens = chat.fastens[fasten.messageId].filter(
              (f) => f.fastensWith !== fasten.fastensWith,
            )
            existingFastens.push(fasten)
            chat.fastens[fasten.messageId] = existingFastens
          }
        }
      }

      // Process markers
      if (markers.length > 0) {
        markers.forEach((marker) => {
          updateStateUsingMarker(state, marker, myJid)
        })
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(PURGE, () => initialState)
      .addCase(upsertChat, (state, action: PayloadAction<UpsertChatAction>) => {
        state[action.payload.chat.jid] = buildMessages(
          state[action.payload.chat.jid],
        )
        return state
      })
      .addCase(
        refreshChats,
        (state, action: PayloadAction<RefreshChatsAction>) => {
          for (const chat of action.payload.chats) {
            state[chat.jid] = {
              ...buildMessages({}),
              ...state[chat.jid],
            }
          }
        },
      )
  },
})

// New type for the processArchivedBatch action
export type ArchivedBatchPayload = {
  messages: Message[]
  corrections: Message[]
  reactions: Reaction[]
  retractions: MessageRetraction[]
  fileShares: MessageFileShared[]
  extractedMetadata: ExtractedMetadata[]
  documentDriverContext: DocumentDriverContext[]
  deliveryOrders: DeliveryOrder[]
  documentLinks: DocumentLink[]
  fastens: Fasten[]
  markers: Marker[]
  myJid: string
  mode: "prepend" | "append"
  // The message an older page was fetched before (infinite scroll).
  anchor?: { chatJid: string; messageId: string }
}

export const {
  addMessage,
  setGpsDocumentLinks,
  addBatchMessageFileShares,
  addBatchMessageRetractions,
  addBatchMessageReactions,
  addMessageCorrection,
  multiAddMessageCorrection,
  addBatchMessagesToChat,
  addBatchExtractedMetadata,
  addBatchDocumentDriverContext,
  addBatchDeliveryOrders,
  addBatchDocumentLinks,
  addBatchFastens,
  addMessageRetraction,
  multiMarkMessage,
  markMessage,
  limitChatSize,
  processArchivedBatch,
} = messagesSlice.actions
export default messagesSlice.reducer
